import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { currencyFormatter } from '@/lib/utils'
import { Dialog, DialogContent } from '@/shad/ui/dialog'
import { MutationStatus } from '@tanstack/react-query'
import { AlertCircleIcon, Award } from 'lucide-react'
import { useState } from 'react'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import { DistributionStatus } from './scholarship-awarding'
import { DistributionPreviewModal } from './distribution-preview-modal'
import { getDistributionSummary } from '../../utils/awarding-method-utils'
import PageLoader from '@/app/common/components/PageLoader'
import { useDistributionPreview } from './use-distribution-preview'

type PurseAwardSectionProps = {
  purse: Purse
  onAward: (purseId: number) => void
  awardStatus: MutationStatus
  distributionStatus: DistributionStatus
  programId: number
}

export function ScholarshipAwardCard({
  purse,
  onAward,
  awardStatus,
  distributionStatus,
  programId,
}: PurseAwardSectionProps) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [showStartedModal, setShowStartedModal] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const distributionSummary = getDistributionSummary(purse)
  const awardPreview = useDistributionPreview({
    programId,
    purseId: purse.id,
    enabled: showPreview || open,
  })

  return (
    <section
      className="bg-white rounded-lg w-full shadow-sm border border-gray-200 p-6 flex flex-col gap-4  mx-auto"
      aria-label={`Award distribution for ${purse.name}`}
    >
      <header className="flex items-center gap-2 mb-2">
        <Award className="size-5 text-primary" aria-hidden="true" />
        <h2 className="text-xl font-semibold text-gray-900">{purse.name}</h2>
      </header>

      {(distributionSummary.heading ||
        distributionSummary.rows.length > 0 ||
        distributionSummary.message) && (
        <div>
          {distributionSummary.heading && (
            <div className="font-medium text-gray-800 mb-2">
              {distributionSummary.heading}
            </div>
          )}

          {distributionSummary.rows.length > 0 && (
            <div className="space-y-1 mb-4">
              {distributionSummary.rows.map((row) => (
                <div key={row.key} className="flex justify-between text-sm">
                  <span>{row.label}</span>
                  <span className="font-medium">
                    {currencyFormatter.format(row.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {distributionSummary.message && (
            <p className="text-sm text-gray-600">
              {distributionSummary.message}
            </p>
          )}
        </div>
      )}
      <div className="mt-auto space-y-4">
        <div className="font-medium text-gray-800 mb-1">Distribution Rules</div>
        <div className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-md p-3">
          <AlertCircleIcon
            className="mt-1 text-yellow-500"
            aria-hidden="true"
          />
          <p className="text-gray-700 text-sm">
            The system will sort the students by the{' '}
            <span className="font-semibold">'sequence number'</span> field in
            ascending order (from lowest to highest).
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            className="flex-1"
            aria-label={`Process award distribution for ${purse.name}`}
            onClick={() => setShowPreview(true)}
            disabled={
              distributionStatus === 'processing' || !purse.awarding_method_code
            }
          >
            <Award className="size-4 mr-2" aria-hidden="true" />
            Start Distribution Process - {purse.name}
          </Button>
        </div>
      </div>

      {/* Distribution Preview Modal - now shown when clicking Award Now */}
      <DistributionPreviewModal
        open={showPreview}
        onOpenChange={setShowPreview}
        purse={purse}
        programId={programId}
        onProceedToAward={() => {
          setShowPreview(false)
          setOpen(true)
        }}
      />

      {/* Award Confirmation Dialog - shown after preview */}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="text-gray-800">
          <PageLoader query={awardPreview}>
            {(distributionPlan) => {
              return (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold">
                      Confirm Award Distribution
                    </h2>
                    <p className="mt-1">
                      This action cannot be undone. Please review the details
                      carefully.
                    </p>
                  </div>
                  <div className="bg-gray-100 border border-gray-200 rounded-2xl p-4">
                    <strong className="text-gray-800">
                      Final confirmation
                    </strong>
                    <div className="text-gray-700">
                      This will distribute{' '}
                      <span className="font-semibold text-amber-600">
                        {currencyFormatter.format(
                          distributionPlan.total_amount
                        )}
                      </span>{' '}
                      in awards.
                      <br />
                      <span className="font-semibold text-amber-600">
                        Important:
                      </span>{' '}
                      Once processed, this action cannot be undone.
                    </div>
                  </div>
                  <div>
                    <label className="block font-medium mb-1 text-sm">
                      Confirm by entering the name of the program (
                      <span className="font-mono ">{purse.name}</span>)
                    </label>
                    <Input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={`Type "${purse.name}" to confirm`}
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setOpen(false)}>
                      Cancel
                    </Button>
                    <div>
                      <SubmitButton
                        variant="primary"
                        status={awardStatus}
                        forceDisabled={input !== purse.name}
                        onClick={() => {
                          onAward(purse.id)
                        }}
                      >
                        Confirm
                      </SubmitButton>
                    </div>
                  </div>
                </div>
              )
            }}
          </PageLoader>
        </DialogContent>
      </Dialog>

      {/* Process Started Confirmation Modal */}
      <Dialog open={showStartedModal} onOpenChange={setShowStartedModal}>
        <DialogContent className="text-gray-800">
          <div className="space-y-4">
            <Award />
            <h2 className="text-lg font-semibold">
              Award Distribution Started
            </h2>
            <p>
              The award distribution process for
              <span className="">{purse.name}</span> has started.
            </p>
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={() => setShowStartedModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
